import { CropRect } from "./crop";

/**
 * Rasterizes an SVG string to a base64 PNG.
 *
 * Two-step approach to avoid canvas tainting in PowerPoint's WKWebView:
 * 1. Draw the field background image directly to canvas (avoids embedding
 *    a large JPEG/PNG inside the SVG data URI which can fail silently).
 * 2. Draw the SVG overlay (shapes/lines/text/pencil) on top.
 *
 * @param svgString    Complete SVG markup (field layer is transparent rect)
 * @param width        Logical width in SVG user units
 * @param height       Logical height in SVG user units
 * @param scale        Pixel density multiplier (2 for insert, 0.25 for thumbnails)
 * @param fieldDataUri Optional field background data URI drawn before SVG
 * @param crop         Optional sub-region of the play to rasterize (logical units)
 * @returns            Base64-encoded PNG string (no data: prefix)
 */
export async function svgToPng(
  svgString: string,
  width: number,
  height: number,
  scale = 2,
  fieldDataUri: string | null = null,
  crop?: CropRect
): Promise<string> {
  const region = crop ?? { x: 0, y: 0, w: width, h: height };
  const canvasW = Math.round(region.w * scale);
  const canvasH = Math.round(region.h * scale);

  if (!canvasW || !canvasH) {
    throw new Error(`Invalid canvas size: ${canvasW}×${canvasH} (play width=${width} height=${height})`);
  }

  const canvas = document.createElement("canvas");
  canvas.width = canvasW;
  canvas.height = canvasH;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get 2d canvas context");

  ctx.scale(scale, scale);
  ctx.translate(-region.x, -region.y);

  // Step 1: draw field background directly (bypasses SVG embedding issues)
  if (fieldDataUri) {
    const fieldImg = await loadImage(fieldDataUri);
    ctx.drawImage(fieldImg, 0, 0, width, height);
  }

  // Step 2: draw SVG overlay (shapes, lines, text, pencil)
  // Strip @font-face blocks — Safari taints the canvas when an SVG drawn via
  // drawImage() contains embedded font data URIs (even though they're data: URLs).
  // Text falls back to system fonts; shapes and lines are unaffected.
  const svgForCanvas = svgString.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "<style></style>");

  const parserError = new DOMParser().parseFromString(svgForCanvas, "image/svg+xml").querySelector("parsererror");
  if (parserError) {
    const lineMatch = parserError.textContent?.match(/line (\d+)/);
    const lineNum = lineMatch ? parseInt(lineMatch[1], 10) : null;
    let context = "";
    if (lineNum) {
      const lines = svgForCanvas.split("\n");
      const start = Math.max(0, lineNum - 3);
      const end = Math.min(lines.length, lineNum + 2);
      context = " | Context: " + lines.slice(start, end).map((l, i) => `[${start + i + 1}] ${l}`).join(" \\n ");
    }
    throw new Error(`Malformed SVG: ${parserError.textContent?.slice(0, 300)}${context}`.slice(0, 1500));
  }

  const svgBase64 = btoa(unescape(encodeURIComponent(svgForCanvas)));
  const dataUri = `data:image/svg+xml;base64,${svgBase64}`;
  const svgImg = await loadImage(dataUri);
  ctx.drawImage(svgImg, 0, 0, width, height);

  let dataUrl: string;
  try {
    dataUrl = canvas.toDataURL("image/png");
  } catch (e) {
    throw new Error(`Canvas tainted (SecurityError): ${e instanceof Error ? e.message : String(e)}`);
  }
  if (!dataUrl || dataUrl === "data:,") {
    throw new Error("Canvas toDataURL returned empty — canvas may be tainted");
  }

  // Strip "data:image/png;base64," prefix — Office.js wants raw base64
  return dataUrl.split(",")[1];
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Image failed to load (src prefix: ${src.slice(0, 40)})`));
    img.src = src;
  });
}
